import type { QuestionStatus, QuestionVisibility, QuestionClaimStatus } from '@models/api/question';
import type {
  EventSummary,
  HeldSpaceSummary,
  SpaceNestedRead,
  SpaceSummary,
  UserSummary,
} from '@models/api';
import type { ISODateTimeString, UUID } from '@models/api/shared';

/**
 * Fields present on QuestionRead / QuestionDetailRead but absent from QuestionProfileRead.
 * Populated when the entity is seeded from a detail or list endpoint; undefined when
 * the only source so far is the profile feed.
 */
export interface QuestionCacheDetail {
  user: UserSummary | null;
  event: EventSummary | null;
  event_ids: UUID[] | null;
  space_id: UUID | null;
  space_ids: UUID[] | null;
  space: SpaceNestedRead | null;
  user_id: UUID | null;
  status: QuestionStatus;
  updated_at?: ISODateTimeString;
  archived_at?: ISODateTimeString | null;
  pinned_at: ISODateTimeString | null;
  pinned_by_user_id: UUID | null;
  pinned_by_name: string | null;
  topic_id: UUID | null;
  topic_name: string | null;
  asked_for_self: boolean;
  asked_for_email: string | null;
  asked_for_user: UserSummary | null;
  created_by_user_id: UUID | null;
  pre_deletion_status?: QuestionStatus | null;
  /** Live Layer v1 (backend `QuestionRead.author_live_event`) — set only by the space feed. */
  author_live_event?: EventSummary | null;
  // Async-matching UX (issue #22): absent from QuestionProfileRead like the rest of this
  // sub-object, so it lives here rather than as a QuestionCacheItem top-level field.
  match_status?: string | null;
  pool_is_thin?: boolean | null;
}

/**
 * Unified question entity stored in the React Query entity store.
 *
 * Universal fields (present in all three API shapes) sit flat at the top level.
 * Fields absent from QuestionProfileRead live under the optional `detail` sub-object.
 * Once `detail` is populated it is never erased — upsertQuestionEntities uses a
 * spread-merge so profile-feed writes only refresh interaction counters.
 */
export interface QuestionCacheItem {
  id: string;
  slug: string | null;
  title: string;
  content: string;
  created_at: ISODateTimeString;
  event_summaries: EventSummary[];
  space_summaries: SpaceSummary[];
  /** Held posts: the viewer's own held Space links. Absent until a response that carries a list
   * lands (the detail, a create or edit response, a profile list), which the backend sends only
   * to the viewer it returns the list to. Only a list overwrites it, and [] means "nothing
   * held". A feed or list row carries null for "not known here", which the mapper drops, so the
   * upsert's spread-merge never erases a known list and the entity never holds null. */
  held_spaces?: HeldSpaceSummary[];

  uplifts_count: number;
  me_too_count: number;
  i_can_help_count: number;

  is_anonymous: boolean;
  is_featured: boolean;
  is_official: boolean;
  visibility: QuestionVisibility;
  is_my_question: boolean;
  has_extra_options: boolean;

  user_has_me_too: boolean;
  user_has_i_can_help: boolean;
  user_has_bookmarked: boolean;

  claim_status: QuestionClaimStatus | null;
  created_by_user: UserSummary | null;
  asked_for_user_id: string | null;

  tag_name?: string;

  detail?: QuestionCacheDetail;
}

export const isQuestionCacheItem = (value: unknown): value is QuestionCacheItem => {
  if (typeof value !== 'object' || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.id === 'string' &&
    typeof obj.title === 'string' &&
    typeof obj.content === 'string' &&
    typeof obj.uplifts_count === 'number' &&
    typeof obj.me_too_count === 'number' &&
    typeof obj.i_can_help_count === 'number' &&
    typeof obj.is_anonymous === 'boolean'
  );
};
